import React, { useState } from 'react';
import { 
  RefreshCw, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Calendar,
  Edit2,
  Save,
  X,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Subscription, Bill, Account, PaymentHistory } from '../../types';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../ui/Pagination';
import PaymentHistoryDetails from './PaymentHistoryDetails';

interface SubscriptionsListProps {
  subscriptions: Subscription[];
  bills: Bill[];
  accounts: Account[];
}

const mockPaymentHistory: PaymentHistory[] = [
  {
    id: '1',
    subscription_id: '1',
    amount: 15.99,
    date: '2025-05-15',
    status: 'completed',
    paymentMethod: 'Credit Card',
    description: 'Monthly payment'
  },
  {
    id: '2',
    subscription_id: '1',
    amount: 15.99,
    date: '2025-04-15',
    status: 'completed',
    paymentMethod: 'Credit Card',
    description: 'Monthly payment'
  }
];

const SubscriptionsList: React.FC<SubscriptionsListProps> = ({
  subscriptions: initialSubscriptions,
  bills: initialBills,
  accounts,
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(initialSubscriptions);
  const [bills, setBills] = useState<Bill[]>(initialBills);
  
  const [subscriptionSort, setSubscriptionSort] = useState<'name' | 'amount' | 'date'>('date');
  const [billSort, setBillSort] = useState<'name' | 'amount' | 'date'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedItem, setSelectedItem] = useState<{ type: 'subscription' | 'bill'; id: string } | null>(null);
  const [editingItem, setEditingItem] = useState<{ type: 'subscription' | 'bill'; id: string } | null>(null);
  const [editForm, setEditForm] = useState<Partial<Subscription | Bill>>({});

  // Add pagination for subscriptions
  const {
    currentPage: subscriptionPage,
    pageSize: subscriptionPageSize,
    totalPages: subscriptionTotalPages,
    paginatedItems: paginatedSubscriptions,
    setCurrentPage: setSubscriptionPage,
    setPageSize: setSubscriptionPageSize
  } = usePagination({
    items: subscriptions,
    initialPageSize: 15
  });

  // Add pagination for bills
  const {
    currentPage: billPage,
    pageSize: billPageSize,
    totalPages: billTotalPages,
    paginatedItems: paginatedBills,
    setCurrentPage: setBillPage,
    setPageSize: setBillPageSize
  } = usePagination({
    items: bills,
    initialPageSize: 15
  });

  const handleSort = (field: 'name' | 'amount' | 'date', type: 'subscription' | 'bill') => {
    if (type === 'subscription') {
      if (subscriptionSort === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSubscriptionSort(field);
        setSortDirection('asc');
      }
    } else {
      if (billSort === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setBillSort(field);
        setSortDirection('asc');
      }
    }
  };

  const handleRowClick = (type: 'subscription' | 'bill', id: string) => {
    setSelectedItem({ type, id });
  };

  const handleEdit = (type: 'subscription' | 'bill', item: Subscription | Bill) => {
    setEditingItem({ type, id: item.id });
    setEditForm(item);
  };

  const handleSave = () => {
    if (!editingItem) return;

    const { type, id } = editingItem;
    const updatedData = {
      ...editForm,
      lastModified: new Date().toISOString()
    };

    if (type === 'subscription') {
      setSubscriptions(prev => 
        prev.map(item => 
          item.id === id ? { ...item, ...updatedData } as Subscription : item
        )
      );
    } else {
      setBills(prev => 
        prev.map(item => 
          item.id === id ? { ...item, ...updatedData } as Bill : item
        )
      );
    }

    setEditingItem(null);
    setEditForm({});
  };

  const handleCancel = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const getSortIcon = (field: string, currentSort: string) => {
    if (field !== currentSort) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  if (selectedItem) {
    const item = selectedItem.type === 'subscription' 
      ? subscriptions.find(s => s.id === selectedItem.id)
      : bills.find(b => b.id === selectedItem.id);

    if (!item) return null;

    return (
      <PaymentHistoryDetails
        item={item}
        type={selectedItem.type}
        paymentHistory={mockPaymentHistory}
        onBack={() => setSelectedItem(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800">
            <tr>
              <th 
                className="px-4 py-3 cursor-pointer"
                onClick={() => handleSort('name', 'subscription')}
              >
                <div className="flex items-center">
                  Name
                  {getSortIcon('name', subscriptionSort)}
                </div>
              </th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Recurring</th>
              <th 
                className="px-4 py-3 cursor-pointer"
                onClick={() => handleSort('amount', 'subscription')}
              >
                <div className="flex items-center">
                  Amount
                  {getSortIcon('amount', subscriptionSort)}
                </div>
              </th>
              <th 
                className="px-4 py-3 cursor-pointer"
                onClick={() => handleSort('date', 'subscription')}
              >
                <div className="flex items-center">
                  Next Due
                  {getSortIcon('date', subscriptionSort)}
                </div>
              </th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSubscriptions.map((subscription) => {
              const account = accounts.find(a => a.id === subscription.account_id);
              const daysUntil = Math.ceil(
                (new Date(subscription.nextCharge).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
              );
              
              return (
                <tr 
                  key={subscription.id} 
                  className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {subscription.name}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: account?.color || '#3B82F6' }}
                      />
                      <span>{account?.name || 'Unknown Account'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" size="sm">
                      {subscription.category}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <RefreshCw size={14} className="mr-1" />
                      {subscription.frequency}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    ${subscription.amount.toFixed(2)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1 text-gray-400" />
                      <span className={daysUntil <= 3 ? 'text-red-600 dark:text-red-400' : ''}>
                        {daysUntil} days
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge 
                      variant={subscription.status === 'active' ? 'success' : 'warning'}
                      size="sm"
                    >
                      {subscription.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRowClick('subscription', subscription.id)}
                    >
                      <ArrowRight size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {paginatedBills.map((bill) => {
              const account = accounts.find(a => a.id === bill.account_id);
              const daysUntil = Math.ceil(
                (new Date(bill.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
              );
              
              return (
                <tr 
                  key={bill.id} 
                  className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {bill.name}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: account?.color || '#3B82F6' }}
                      />
                      <span>{account?.name || 'Unknown Account'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" size="sm">
                      {bill.category}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {bill.isRecurring && (
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <RefreshCw size={14} className="mr-1" />
                        {bill.frequency}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    ${bill.amount.toFixed(2)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1 text-gray-400" />
                      <span className={daysUntil <= 3 ? 'text-red-600 dark:text-red-400' : ''}>
                        {daysUntil} days
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge 
                      variant={
                        bill.status === 'paid' ? 'success' :
                        bill.status === 'pending' ? 'warning' : 'danger'
                      }
                      size="sm"
                    >
                      {bill.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRowClick('bill', bill.id)}
                    >
                      <ArrowRight size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4">
        <Pagination
          currentPage={subscriptionPage}
          totalPages={subscriptionTotalPages}
          pageSize={subscriptionPageSize}
          totalItems={subscriptions.length + bills.length}
          onPageChange={setSubscriptionPage}
          onPageSizeChange={setSubscriptionPageSize}
        />
      </div>
    </div>
  );
};

export default SubscriptionsList;